import { useEffect } from 'react';
import {
  VAPID_KEY,
  getFirebaseConfigForSw,
  isFirebaseConfigured,
  requestFCMToken,
  subscribeFCMForeground,
} from '../firebase';
import { getSupabase, isSupabaseConfigured } from '../lib/supabase';

function isAsciiHeaderSafe(value) {
  const s = String(value ?? '');
  for (let i = 0; i < s.length; i += 1) {
    if (s.charCodeAt(i) > 255) return false;
  }
  return true;
}

function buildMessagingSwUrl() {
  const cfg = getFirebaseConfigForSw();
  const q = new URLSearchParams({
    apiKey: cfg.apiKey || '',
    authDomain: cfg.authDomain || '',
    projectId: cfg.projectId || '',
    storageBucket: cfg.storageBucket || '',
    messagingSenderId: cfg.messagingSenderId || '',
    appId: cfg.appId || '',
  });
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}/firebase-messaging-sw.js?${q.toString()}`;
}

/**
 * FCM 토큰 발급 → users.fcm_token 저장, 포그라운드 알림 표시.
 * Firebase 오류는 앱을 중단시키지 않음.
 * @param {string | null | undefined} userId public.users.id
 */
export function useFCM(userId) {
  useEffect(() => {
    if (!userId || !isSupabaseConfigured || !isFirebaseConfigured()) return undefined;

    const vapidKey = VAPID_KEY.trim();
    if (!vapidKey || !isAsciiHeaderSafe(vapidKey)) return undefined;
    const cfg = getFirebaseConfigForSw();
    if (
      !isAsciiHeaderSafe(cfg.apiKey) ||
      !isAsciiHeaderSafe(cfg.projectId) ||
      !isAsciiHeaderSafe(cfg.messagingSenderId) ||
      !isAsciiHeaderSafe(cfg.appId)
    ) {
      return undefined;
    }
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return undefined;

    let cancelled = false;
    /** @type {ServiceWorkerRegistration | undefined} */
    let swRegistration;

    async function registerToken() {
      try {
        const permission = await Notification.requestPermission();
        if (cancelled || permission !== 'granted') return;

        try {
          swRegistration = await navigator.serviceWorker.register(buildMessagingSwUrl(), {
            scope: '/',
          });
          await navigator.serviceWorker.ready;
        } catch (err) {
          console.warn('[useFCM] service worker register failed', err?.message || err);
          return;
        }

        const token = await requestFCMToken(vapidKey, swRegistration);
        if (cancelled || !token) return;

        console.log('[FCM] 토큰 발급 성공', `${token.slice(0, 20)}...`);

        const sb = getSupabase();
        if (!sb) return;

        const { error } = await sb
          .from('users')
          .update({
            fcm_token: token,
            fcm_updated_at: new Date().toISOString(),
          })
          .eq('id', userId);

        if (error) {
          console.warn('[useFCM] token save failed', error.message);
        } else {
          console.log('[FCM] 토큰 저장 완료');
        }

        await subscribeFCMForeground((payload) => {
          if (cancelled) return;
          const n = payload.notification || {};
          const title = n.title || payload.data?.title || 'Mingo';
          const body = n.body || payload.data?.body || '';
          if (Notification.permission === 'granted') {
            const opts = { body, icon: n.icon || '/logo192.png', data: payload.data || {} };
            if (swRegistration?.showNotification) {
              void swRegistration.showNotification(title, opts);
            } else {
              new Notification(title, opts);
            }
          }
        });
      } catch (err) {
        console.warn('[useFCM] register failed', err?.message || err);
      }
    }

    void registerToken();

    return () => {
      cancelled = true;
    };
  }, [userId]);
}
